import * as React from 'react'
import { FormGroup, Position, Tag, TextArea, Switch } from '@blueprintjs/core'
import { useDepopChannelFormContext } from '../../../../products_v4/listings/depop/DepopChannelFormContext'
import { ChannelPriceInput, SharedQuantityInput } from '../ChannelShared/SharedInputs'
import { DepopSpecificSelect } from './DepopSpecificSelect'
import CHANNELS from '../../../../../constants/Channels'
import CategoryMarketCategoryPickerV2 from '../../../../../components/Categories/CategoryMarketCategoryPickerV2'
import ListingDurationSelect from '../../../../../components/selects/channels/depop/ListingDurationSelect'
import { stylizeTitle, renderError, USE_PRODUCT_VALUE, stripHtmlToPlainText } from '../Helpers'
import { Specifics } from './Specifics'
import { SizeSelect } from './SizeSelect'
import { useDepopSpecifics } from './useDepopSpecifics'
import { useDepopSizes } from './useDepopSizes'
import NullSafeNumericInput from '../../../../../components/NullSafeNumericInput'
import { GutterTooltipError } from '../GutterTooltipError'
import Client from '../../../../../client'
import { LoadableSelect } from '../../../../../components/Inputs'

// Depop has no title of its own — it derives one from the first line of the description — but the
// product title is still what we store and show, so cap it the way the other channels do.
export const DEPOP_MAX_TITLE_LENGTH = 140
export const DEPOP_MAX_DESCRIPTION_LENGTH = 1000
export const DEPOP_AUTO_NEGOTIATE_MIN_DISCOUNT_FRACTION = 0.05

// Static templates are HTML elsewhere; Depop only accepts plain text, so strip tags then append.
export const appendStrippedStaticTemplate = (currentDescription: string, templateHtml: string): string => {
  const plainBody = stripHtmlToPlainText(templateHtml).replace(/\n+$/g, '')
  if (!plainBody) return currentDescription
  if (!currentDescription) return plainBody
  const separator = currentDescription.endsWith('\n') ? '' : '\n'
  return `${currentDescription}${separator}${plainBody}`
}

interface Props {
  sectionOpen: boolean
  skipErrorPrefix?: boolean
  showDescription?: boolean
  productDescription?: string
}

export const DepopForm = React.memo((props: Props) => {
  const { skipErrorPrefix, sectionOpen, showDescription = true, productDescription } = props
  const { form, onListingChange, errors, productUnitTracking } = useDepopChannelFormContext()
  const { valuesByName } = useDepopSpecifics()
  const depopSizes = useDepopSizes(form.categoryMarketCategoryId)
  const sizeCellShown =
    !!depopSizes.error || depopSizes.loading || (!!depopSizes.sizeSet.sizeSetId && depopSizes.sizeSet.sizes.length > 0)
  const [templateValue, setTemplateValue] = React.useState<number | null>(null)
  const [insertingTemplate, setInsertingTemplate] = React.useState(false)

  const handleCategoryChange = React.useCallback(
    (v: number) => {
      onListingChange({ name: 'categoryMarketCategoryId', value: v })
    },
    [onListingChange]
  )

  const categoryError = skipErrorPrefix ? errors.categoryMarketCategoryId : errors.depopListingCategoryMarketCategoryId
  const descriptionError = skipErrorPrefix ? errors.description : errors.depopListingDescription
  const autoNegotiateError = skipErrorPrefix
    ? errors.autoNegotiateOfferPrice
    : errors.depopListingAutoNegotiateOfferPrice
  const usingProductDescription = form.description === USE_PRODUCT_VALUE
  const descriptionValue = usingProductDescription
    ? stripHtmlToPlainText(productDescription || '')
    : form.description || ''
  const descriptionValueRef = React.useRef(descriptionValue)
  descriptionValueRef.current = descriptionValue
  const descriptionTooLong = descriptionValue.length > DEPOP_MAX_DESCRIPTION_LENGTH

  const onProcessTemplates = React.useCallback(
    (state: { templates: { id: number; title: string }[] }) =>
      state.templates.map(s => ({ value: s.id, label: s.title })),
    []
  )

  const onSelectTemplate = React.useCallback(
    async (templateId: number) => {
      setInsertingTemplate(true)
      try {
        const body = await Client.getTemplate(templateId).then(response => response.data.template.body)
        // Read latest description after the await so edits during fetch are not overwritten.
        const nextDescription = appendStrippedStaticTemplate(descriptionValueRef.current, body)
        onListingChange({ name: 'description', value: nextDescription })
        setTemplateValue(templateId)
        analytics.track('Static Template Inserted', { channel: 'depop', plainText: true })
      } finally {
        setInsertingTemplate(false)
      }
    },
    [onListingChange]
  )

  return (
    <div className="depop-form">
      {showDescription && (
        <FormGroup
          label={stylizeTitle('Description', descriptionError)}
          labelInfo="Required"
          helperText={
            renderError(descriptionError) || 'Depop does not support HTML. Static templates are inserted as plain text.'
          }
          className="stretch-full float-right-formgroup-label"
          intent={descriptionError || descriptionTooLong ? 'danger' : 'none'}
        >
          <TextArea
            fill
            growVertically
            large
            rows={6}
            value={descriptionValue}
            disabled={insertingTemplate}
            intent={descriptionError || descriptionTooLong ? 'danger' : 'none'}
            onChange={e => onListingChange({ name: 'description', value: e.target.value })}
            style={{ resize: 'vertical' }}
          />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
            <div style={{ flex: '1 1 auto', maxWidth: '50%' }}>
              <LoadableSelect
                value={templateValue}
                onChange={onSelectTemplate}
                clearable={false}
                disabled={insertingTemplate}
                load={() => Client.templateIndex({ draft: false, static: true })}
                placeholder="Insert Template"
                process={onProcessTemplates}
                autoload
                noLabel
              />
            </div>
            <Tag minimal intent={descriptionTooLong ? 'danger' : 'none'}>
              {`${descriptionValue.length}/${DEPOP_MAX_DESCRIPTION_LENGTH}`}
            </Tag>
          </div>
        </FormGroup>
      )}
      <div className="stretch-full">
        <CategoryMarketCategoryPickerV2
          marketId={CHANNELS.DEPOP.id}
          style={undefined}
          label={stylizeTitle('Category', categoryError)}
          error={categoryError}
          showRequiredLabel
          value={form.categoryMarketCategoryId}
          onChange={handleCategoryChange}
          errorTooltipOffset={sectionOpen && 45}
          simpleValue
        />
      </div>
      <ChannelPriceInput
        error={skipErrorPrefix ? errors.price : errors.depopListingPrice}
        showRequiredLabel
        value={form.price}
        onListingChange={onListingChange}
        errorTooltipOffset={sectionOpen && 45}
      />
      <FormGroup
        label={stylizeTitle('Auto-negotiate offer price', autoNegotiateError)}
        helperText={
          renderError(autoNegotiateError) ||
          'Buyer offers at or above this price are accepted; lower offers are countered with this price. Must be at least 5% below the listing price. Leave blank to respond manually.'
        }
      >
        <GutterTooltipError isOpen={autoNegotiateError} offsetPx={sectionOpen && 380} position={Position.LEFT}>
          <NullSafeNumericInput
            min={0.01}
            placeholder="Optional"
            clampValueOnBlur
            leftIcon="dollar"
            minorStepSize={0.01}
            selectAllOnFocus
            name="autoNegotiateOfferPrice"
            onValueChange={(_, str) =>
              onListingChange({
                name: 'autoNegotiateOfferPrice',
                value: str === '' || str == null ? null : str
              })
            }
            value={form.autoNegotiateOfferPrice ?? null}
            intent={autoNegotiateError ? 'danger' : 'none'}
            fill
          />
        </GutterTooltipError>
      </FormGroup>
      <SharedQuantityInput
        error={skipErrorPrefix ? errors.quantity : errors.depopListingQuantity}
        minorStepSize={1}
        min={1}
        value={form.quantity}
        disabled={productUnitTracking}
        helperText={productUnitTracking ? 'Unit-tracking products automatically manage quantity' : ''}
        onListingChange={onListingChange}
        errorTooltipOffset={sectionOpen && 196}
        errorTooltipPosition={Position.RIGHT}
      />
      <FormGroup
        label={stylizeTitle('Duration', skipErrorPrefix ? errors.duration : errors.depopListingDuration)}
        helperText={renderError(skipErrorPrefix ? errors.duration : errors.depopListingDuration)}
      >
        <ListingDurationSelect
          placeholder="Leave blank for indefinite"
          value={form.duration}
          onChange={value => onListingChange({ name: 'duration', value })}
          disabled={form.isConcurrent}
        />
      </FormGroup>
      {/* Not ChannelSelectInput: it parseInts the selected value, which is fine for the numeric
          profile ids Etsy uses but turns a Depop condition into NaN. */}
      <DepopSpecificSelect
        specificName="Condition"
        value={form.condition}
        options={valuesByName.condition || []}
        required
        error={skipErrorPrefix ? errors.condition : errors.depopListingCondition}
        errorTooltipOffset={sectionOpen && 380}
        errorTooltipPosition={Position.LEFT}
        onChange={v => onListingChange({ name: 'condition', value: v })}
      />
      {/* Required whenever it renders - SizeSelect shows nothing for unsized categories. */}
      <SizeSelect
        depopSizes={depopSizes}
        required
        error={skipErrorPrefix ? errors.sizeId : errors.depopListingSizeId}
        errorTooltipOffset={sectionOpen && 196}
        errorTooltipPosition={Position.RIGHT}
      />
      <Specifics
        sectionOpen={sectionOpen}
        cellsBefore={sizeCellShown ? 6 : 5}
        brandError={skipErrorPrefix ? errors.brand : errors.depopListingBrand}
      />
      <FormGroup className="stretch-full">
        <Switch
          checked={!!form.isBoosted}
          label="Boost this listing on Depop for additional fee"
          onChange={() => onListingChange({ name: 'isBoosted', value: !form.isBoosted })}
        />
      </FormGroup>
    </div>
  )
})
